// Home, Activity this week and Bin: response shapes and the date maths shared by server and browser.
// Every calendar calculation takes an IANA time zone so the server can answer in the viewer's local days.

import { BIN_DAYS } from './constants';
import type { ActivityKind } from './constants';
import type { BinType, CompanyRef, FollowupItem, Ref } from './types';

export const DAY_MS = 86_400_000;
/** Follow-ups due within this many days after today show under "Upcoming"; later ones are only counted. */
export const UPCOMING_DAYS = 7;

/* ───────────────────────── Time zones ───────────────────────── */

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(tz, f);
  }
  return f;
}

export function isTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 64) return false;
  try {
    formatter(tz);
    return true;
  } catch {
    return false;
  }
}

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** The wall-clock reading of `d` in `tz`. */
export function zonedParts(d: Date, tz: string): ZonedParts {
  const out: ZonedParts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const p of formatter(tz).formatToParts(d)) {
    if (p.type in out) out[p.type as keyof ZonedParts] = Number(p.value);
  }
  if (out.hour === 24) out.hour = 0;
  return out;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "2026-10-16": the calendar day `d` falls on in `tz`. */
export function localDayKey(d: Date, tz: string): string {
  const p = zonedParts(d, tz);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

function keyToUtc(key: string): number {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

function utcToKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function addDays(key: string, n: number): string {
  return utcToKey(keyToUtc(key) + n * DAY_MS);
}

/** Whole days from `from` to `to`, both day keys. */
export function daysBetween(from: string, to: string): number {
  return Math.round((keyToUtc(to) - keyToUtc(from)) / DAY_MS);
}

/** 0 for Monday through 6 for Sunday. */
export function weekdayOf(key: string): number {
  return (new Date(keyToUtc(key)).getUTCDay() + 6) % 7;
}

function offsetAt(ms: number, tz: string): number {
  const p = zonedParts(new Date(ms), tz);
  const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return wall - Math.floor(ms / 1000) * 1000;
}

/** The instant the clocks in `tz` read `hhmm` on day `key`. Times skipped by a DST change move forward. */
export function zonedTime(key: string, hhmm: string, tz: string): Date {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  const guess = keyToUtc(key) + (h * 60 + m) * 60_000;
  const first = offsetAt(guess, tz);
  let at = guess - first;
  const second = offsetAt(at, tz);
  if (second !== first) at = guess - second;
  return new Date(at);
}

export function startOfDay(key: string, tz: string): Date {
  return zonedTime(key, '00:00', tz);
}

/* ───────────────────────── Follow-up due dates ───────────────────────── */

export type DueBucket = 'overdue' | 'today' | 'upcoming' | 'later';

type Due = Pick<FollowupItem, 'dueAt' | 'hasTime'>;

/** Date-only follow-ups are stored at noon UTC on their day, so their day is the UTC date. */
export function dueDayKey(f: Due, tz: string): string {
  return f.hasTime ? localDayKey(new Date(f.dueAt), tz) : f.dueAt.slice(0, 10);
}

export function dueBucket(f: Due, now: Date, tz: string): DueBucket {
  const today = localDayKey(now, tz);
  const key = dueDayKey(f, tz);
  if (key < today) return 'overdue';
  if (key === today) return 'today';
  return daysBetween(today, key) <= UPCOMING_DAYS ? 'upcoming' : 'later';
}

/** 'overdue' once the moment has passed (or the day, for date-only items), 'soon' within the hour. */
export function dueState(f: Due, now: Date, tz: string): 'overdue' | 'soon' | null {
  if (!f.hasTime) return dueDayKey(f, tz) < localDayKey(now, tz) ? 'overdue' : null;
  const left = new Date(f.dueAt).getTime() - now.getTime();
  if (left < 0) return 'overdue';
  return left <= 60 * 60_000 ? 'soon' : null;
}

/** The end of the Upcoming window: the start of the day after the last upcoming day. */
export function upcomingEnd(now: Date, tz: string): Date {
  return startOfDay(addDays(localDayKey(now, tz), UPCOMING_DAYS + 1), tz);
}

/* ───────────────────────── Snooze and quick dates ───────────────────────── */

export interface DueChoice {
  id: string;
  label: string;
  sub: string;
  /** ISO timestamp for timed choices, "YYYY-MM-DD" for whole days. */
  dueAt: string;
  hasTime: boolean;
  /** The instant used for ordering and comparisons. */
  at: string;
}

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mon 19 Oct" for a day key. */
export function shortDay(key: string): string {
  const [, m, d] = key.split('-').map(Number) as [number, number, number];
  return `${SHORT_DAYS[weekdayOf(key)]} ${d} ${SHORT_MONTHS[m - 1]}`;
}

function timed(id: string, label: string, sub: string, key: string, hhmm: string, tz: string): DueChoice {
  const at = zonedTime(key, hhmm, tz).toISOString();
  return { id, label, sub, dueAt: at, hasTime: true, at };
}

function wholeDay(id: string, label: string, sub: string, key: string): DueChoice {
  return { id, label, sub, dueAt: key, hasTime: false, at: `${key}T12:00:00.000Z` };
}

/** The next Monday strictly after `key`. */
export function nextMonday(key: string): string {
  return addDays(key, 7 - weekdayOf(key));
}

/**
 * Snooze menu choices. "Later today" is 16:00 before 14:00, otherwise two hours from now rounded up to the
 * half hour, and disappears from 21:00. Monday is left out when it is tomorrow or a week from today.
 */
export function snoozePresets(now: Date, tz: string): DueChoice[] {
  const today = localDayKey(now, tz);
  const p = zonedParts(now, tz);
  const minutes = p.hour * 60 + p.minute;
  const out: DueChoice[] = [];
  if (minutes < 21 * 60) {
    let target = 16 * 60;
    if (minutes >= 14 * 60) target = Math.ceil((minutes + 120) / 30) * 30;
    if (target < 24 * 60) {
      const hhmm = `${pad(Math.floor(target / 60))}:${pad(target % 60)}`;
      out.push(timed('later', 'Later today', hhmm, today, hhmm, tz));
    }
  }
  const tomorrow = addDays(today, 1);
  out.push(timed('tomorrow', 'Tomorrow', `${SHORT_DAYS[weekdayOf(tomorrow)]} 09:00`, tomorrow, '09:00', tz));
  const monday = nextMonday(today);
  const week = addDays(today, 7);
  if (monday !== tomorrow && monday !== week) out.push(timed('monday', 'Monday', `${shortDay(monday)}, 09:00`, monday, '09:00', tz));
  out.push(wholeDay('week', 'Next week', shortDay(week), week));
  return out;
}

/** Quick picks in the follow-up dialog: whole days, so a time can be added separately. */
export function quickDates(now: Date, tz: string): DueChoice[] {
  const today = localDayKey(now, tz);
  const tomorrow = addDays(today, 1);
  const monday = nextMonday(today);
  const week = addDays(today, 7);
  return [
    wholeDay('today', 'Today', shortDay(today), today),
    wholeDay('tomorrow', 'Tomorrow', shortDay(tomorrow), tomorrow),
    // On a Monday, next Monday is the same day as "In a week".
    ...(monday === week ? [] : [wholeDay('monday', monday === tomorrow ? 'Monday' : 'Next Monday', shortDay(monday), monday)]),
    wholeDay('week', 'In a week', shortDay(week), week),
  ];
}

/* ───────────────────────── Weeks ───────────────────────── */

export function weekStartKey(dayKey: string): string {
  return addDays(dayKey, -weekdayOf(dayKey));
}

export interface WeekRange {
  /** Monday, as a day key. */
  key: string;
  start: Date;
  /** The following Monday at midnight. */
  end: Date;
  days: { key: string; start: Date; end: Date }[];
}

export function weekRange(startKey: string, tz: string): WeekRange {
  const key = weekStartKey(startKey);
  const days = Array.from({ length: 7 }, (_, i) => {
    const k = addDays(key, i);
    return { key: k, start: startOfDay(k, tz), end: startOfDay(addDays(k, 1), tz) };
  });
  return { key, start: days[0]!.start, end: days[6]!.end, days };
}

/** "12–18 Oct", "28 Sep – 4 Oct", "29 Dec 2025 – 4 Jan 2026" */
export function weekLabel(key: string): string {
  const end = addDays(key, 6);
  const [y1, m1, d1] = key.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = end.split('-').map(Number) as [number, number, number];
  if (y1 !== y2) return `${d1} ${SHORT_MONTHS[m1 - 1]} ${y1} – ${d2} ${SHORT_MONTHS[m2 - 1]} ${y2}`;
  if (m1 !== m2) return `${d1} ${SHORT_MONTHS[m1 - 1]} – ${d2} ${SHORT_MONTHS[m2 - 1]}`;
  return `${d1}–${d2} ${SHORT_MONTHS[m1 - 1]}`;
}

export interface WeekOption {
  key: string;
  label: string;
  sub: string;
}

/** This week, last week and the weeks before, newest first. */
export function weekOptions(now: Date, tz: string, count: number): WeekOption[] {
  const current = weekStartKey(localDayKey(now, tz));
  return Array.from({ length: count }, (_, i) => {
    const key = addDays(current, -7 * i);
    const label = i === 0 ? 'This week' : i === 1 ? 'Last week' : `${i} weeks ago`;
    return { key, label, sub: weekLabel(key) };
  });
}

export function dayName(key: string): string {
  return DAY_NAMES[weekdayOf(key)]!;
}

/* ───────────────────────── Bin ───────────────────────── */

/** Whole days until the daily clean-up removes an item deleted at `deletedAt`. */
export function daysLeft(deletedAt: string | Date, now: Date): number {
  const end = new Date(deletedAt).getTime() + BIN_DAYS * DAY_MS;
  return Math.max(0, Math.ceil((end - now.getTime()) / DAY_MS));
}

/* ───────────────────────── Responses ───────────────────────── */

export interface HomeMeeting {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location: string | null;
  meetUrl: string | null;
  htmlLink: string | null;
  mine: boolean;
  owner: Ref | null;
  people: Ref[];
  company: Ref | null;
  /** Number of attendees beyond the named people. */
  others: number;
}

export interface HomeDealStep {
  id: string;
  name: string;
  step: string;
  at: string;
  hasTime: boolean;
  owner: Ref | null;
  company: Ref | null;
  person: Ref | null;
  value: number;
  currency: string;
  stage: string;
}

export interface HomeLater {
  kind: 'meeting' | 'step';
  id: string;
  at: string;
  hasTime: boolean;
  title: string;
  sub: string;
  mine: boolean;
  personId: string | null;
  companyId: string | null;
  dealId: string | null;
}

export interface HomeReply {
  threadId: string;
  subject: string;
  snippet: string;
  at: string;
  from: string;
  person: Ref | null;
  company: Ref | null;
  mailbox: string;
  personal: boolean;
}

/** A recent tracked conversation of yours that is not waiting on you: a reply you read, or an email of yours that was opened. */
export interface HomeRecentThread {
  threadId: string;
  subject: string;
  at: string;
  /** The contact's name, or the other side's address when they are not a contact. */
  name: string;
  person: Ref | null;
  /** 'in' when they wrote last, 'out' when you did. */
  last: 'in' | 'out';
  /** How often your latest email in it was probably opened; 0 when it was not, or was sent untracked. */
  opens: number;
}

export interface HomeStage {
  id: string;
  name: string;
  color: string;
  count: number;
  value: number;
}

export interface HomeQuietDeal {
  id: string;
  name: string;
  company: Ref | null;
  owner: Ref | null;
  days: number;
  value: number;
}

export interface HomeQuietPerson {
  id: string;
  name: string;
  hue: number;
  company: Ref | null;
  owner: Ref | null;
  days: number | null;
}

export interface HomeNewItem {
  type: 'person' | 'company' | 'deal';
  id: string;
  name: string;
  hue: number;
  /** Companies only: the logo from their website, as a logo id. */
  logo?: string | null;
  sub: string;
  at: string;
  by: Ref | null;
  companyId: string | null;
  /** Deals only. */
  value?: number;
  currency?: string;
}

export interface HomeFinderSearch {
  id: string;
  description: string;
  status: string;
  progress: number;
  waiting: number;
  duplicates: number;
  by: Ref | null;
  at: string;
  finishedAt: string | null;
}

export interface HomeWonDeal {
  id: string;
  name: string;
  company: string | null;
  value: number;
  at: string;
}

export interface HomeData {
  now: string;
  tz: string;
  followups: {
    open: (FollowupItem & { bucket: DueBucket })[];
    doneToday: number;
    laterCount: number;
    team: { user: Ref; count: number }[];
  };
  next: {
    meeting: HomeMeeting | null;
    step: HomeDealStep | null;
    later: HomeLater[];
    todayCount: number;
  };
  inbox: {
    replies: HomeReply[];
    replyCount: number;
    /** Up to three more recent conversations of yours from the last 14 days, newest first. */
    recent: HomeRecentThread[];
    triage: { count: number; names: Ref[] };
    hasMailbox: boolean;
  };
  pipeline: {
    currency: string;
    openValue: number;
    openCount: number;
    stages: HomeStage[];
    wonMonth: { value: number; count: number };
  };
  quiet: {
    staleAfterDays: number;
    deals: HomeQuietDeal[];
    dealCount: number;
    people: HomeQuietPerson[];
    peopleCount: number;
  };
  fresh: {
    since: string;
    items: HomeNewItem[];
    counts: { person: number; company: number; deal: number };
  };
  finder: { searches: HomeFinderSearch[]; waiting: number };
  won: {
    since: string;
    value: number;
    count: number;
    deals: HomeWonDeal[];
    lost: { count: number; value: number; names: string[] };
  };
  week: {
    start: string;
    end: string;
    emailsSent: number;
    replies: number;
    meetings: number;
    followupsDone: number;
    perUser: { user: Ref; count: number }[];
  };
}

export interface LinkRefs {
  person: (Ref & { companyId: string | null }) | null;
  company: CompanyRef | null;
  deal: (Ref & { companyId: string | null; personId: string | null; company: string | null }) | null;
}

export interface ActivityEntry {
  id: string;
  kind: ActivityKind;
  at: string;
  userId: string | null;
  person: Ref | null;
  company: Ref | null;
  deal: { id: string; name: string } | null;
  meta: Record<string, unknown>;
}

export interface ActivityWeek {
  week: string;
  start: string;
  end: string;
  currency: string;
  updatedAt: string;
  days: { key: string; counts: Partial<Record<ActivityKind, number>>; total: number }[];
  totals: Partial<Record<ActivityKind, number>>;
  won: { count: number; value: number; deals: { id: string; name: string; value: number; userId: string | null; at: string }[] };
  newDeals: { id: string; name: string; value: number; userId: string | null; at: string }[];
  replies: { count: number; latest: ActivityEntry[] };
  /** One block per active teammate. */
  users: {
    user: Ref;
    counts: Partial<Record<ActivityKind, number>>;
    total: number;
    wonValue: number;
    entries: ActivityEntry[];
  }[];
  /** Entries by the system and by anyone who is not an active teammate ("System and former teammates"). */
  other: { counts: Partial<Record<ActivityKind, number>>; total: number; entries: ActivityEntry[] };
}

export type BinEntryType = BinType | 'tag';

export interface BinEntry {
  type: BinEntryType;
  id: string;
  name: string;
  hue: number | null;
  sub: string;
  /** The record this item belongs to, for notes, comments, files and follow-ups. */
  on: { type: 'person' | 'company' | 'deal'; id: string; name: string } | null;
  deletedAt: string;
  deletedBy: Ref | null;
  daysLeft: number;
}

export interface BinList {
  items: BinEntry[];
  days: number;
}

export interface PurgeResult {
  purged: { type: BinEntryType; id: string }[];
  skipped: { type: BinEntryType; id: string; name: string; reason: 'storage' | 'blocked' | 'missing' }[];
}
